import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { SparkCreatorVideoDto } from '@/app/common/general-types/spark-ads-v3.dto';
import { FormatService } from '@/app/common/format/format.service';

/**
 * How the Spark ad will look in the TikTok feed.
 *
 * Uses the real post thumbnail and the real engagement counts, because a Spark ad
 * carries the original post's likes and comments across.
 */
@Component({
  selector: 'sa-phone-preview',
  imports: [],
  templateUrl: './phone-preview.html',
  styleUrl: './phone-preview.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhonePreview {
  readonly entry = input<SparkCreatorVideoDto | undefined>(undefined);
  readonly callToActionLabel = input.required<string>();

  private readonly formatService = inject(FormatService);

  protected readonly handle = computed(() => {
    const handle = this.entry()?.handle ?? this.entry()?.video.creator_info?.handle_name;
    if (!handle) {
      return '@your.creator';
    }
    return handle.startsWith('@') ? handle : `@${handle}`;
  });

  protected readonly thumbnailUrl = computed(
    () => this.entry()?.video.video_info?.thumbnail_url ?? '',
  );

  protected readonly likes = computed(() =>
    this.formatService.convertIntToString(this.entry()?.video.likes ?? 0),
  );

  protected readonly comments = computed(() =>
    this.formatService.convertIntToString(this.entry()?.video.comments ?? 0),
  );

  protected readonly shares = computed(() =>
    this.formatService.convertIntToString(this.entry()?.video.shares ?? 0),
  );

  /** The three checks the panel reports under the phone. */
  protected readonly moderationLabel = computed(() => {
    switch (this.entry()?.moderation.status) {
      case 'cleared':
        return 'Passed';
      case 'in_moderation':
        return 'In review';
      case 'declined':
        return 'Declined';
      default:
        return 'Unknown';
    }
  });

  protected readonly moderationIsGood = computed(
    () => this.entry()?.moderation.status === 'cleared',
  );

  protected readonly permissionLabel = computed(() => {
    const auth = this.entry()?.auth;
    if (!auth) {
      return 'Not requested';
    }
    switch (auth.state) {
      case 'authorised':
        return auth.daysUntilExpiry !== null ? `${auth.daysUntilExpiry} days left` : 'Authorised';
      case 'requested':
        return 'Requested';
      case 'rejected':
        return 'Declined';
      case 'expired':
        return 'Expired';
      case 'unknown':
        return 'Unknown';
      default:
        return 'Not requested';
    }
  });

  protected readonly permissionIsGood = computed(
    () => this.entry()?.auth.state === 'authorised',
  );

  protected valueClass(isGood: boolean): string {
    return isGood ? 'text-green-400' : 'text-amber-300';
  }
}
